import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  intentTopBreakdownQueryOptions,
  negativeFeedbackQueryOptions,
  positiveFeedbackQueryOptions,
} from "@/hooks/use-analysis";
import type {
  AnalysisFilters,
  IntentSubRow,
  IntentTopBreakdownRow,
  SentimentDistribution,
  SentimentLabelRow,
} from "@/types/analysis";

type Selection =
  | { kind: "top"; top: string }
  | { kind: "negative" }
  | { kind: "positive" };

interface Props {
  filters: AnalysisFilters;
  onTopClick?: (top: string) => void;
  onSubClick?: (leaf: string) => void;
  onNegativeClick?: (label: string) => void;
  onPositiveClick?: (label: string) => void;
}

function formatNumber(value: number): string {
  return value.toLocaleString("ko-KR");
}

function NavItem({
  label,
  count,
  active,
  onSelect,
  tone,
}: {
  label: string;
  count: number;
  active: boolean;
  onSelect: () => void;
  tone?: "negative" | "positive";
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors",
        active
          ? "bg-accent font-medium"
          : "hover:bg-muted/60 text-muted-foreground hover:text-foreground",
      )}
    >
      <span className="truncate text-left">{label}</span>
      <Badge
        variant={active ? "default" : "secondary"}
        className={cn(
          "tabular-nums",
          !active && tone === "negative" &&
            "bg-red-500/15 text-red-700 hover:bg-red-500/20",
          !active && tone === "positive" &&
            "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20",
        )}
      >
        {formatNumber(count)}
      </Badge>
    </button>
  );
}

function SubListItem({
  row,
  topTotal,
  onClick,
}: {
  row: IntentSubRow;
  topTotal: number;
  onClick?: () => void;
}) {
  const ratio = topTotal > 0 ? (row.count / topTotal) * 100 : 0;
  return (
    <li>
      <button
        type="button"
        disabled={!onClick}
        onClick={onClick}
        className="-mx-2 flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-sm enabled:hover:bg-muted/60 enabled:cursor-pointer disabled:cursor-default text-left"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{row.subKo || row.sub}</span>
          {row.subKo && row.subKo !== row.sub && (
            <span className="truncate text-[11px] text-muted-foreground">
              {row.sub}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 tabular-nums">
          <span className="text-xs text-muted-foreground">
            {ratio.toFixed(1)}%
          </span>
          <span className="w-12 text-right">{formatNumber(row.count)}</span>
        </div>
      </button>
    </li>
  );
}

function SelectedTopDetail({
  row,
  onTopClick,
  onSubClick,
}: {
  row: IntentTopBreakdownRow;
  onTopClick?: (top: string) => void;
  onSubClick?: (leaf: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3 border-b pb-3">
        <div className="space-y-0.5">
          <h3 className="text-base font-semibold">{row.topKo || row.top}</h3>
          {row.topKo && row.topKo !== row.top && (
            <p className="text-xs text-muted-foreground">{row.top}</p>
          )}
        </div>
        <button
          type="button"
          disabled={!onTopClick}
          onClick={() => onTopClick?.(row.top)}
          className="flex items-center gap-2 rounded-md border px-3 py-1 text-xs enabled:hover:bg-muted/60 enabled:cursor-pointer disabled:cursor-default disabled:opacity-60"
        >
          <span className="text-muted-foreground">전체 메시지</span>
          <span className="tabular-nums font-medium">
            {formatNumber(row.total)}
          </span>
        </button>
      </div>

      {row.subs.length === 0 ? (
        <p className="text-sm text-muted-foreground">하위 라벨이 없습니다.</p>
      ) : (
        <ul className="divide-y">
          {row.subs.map((sub) => (
            <SubListItem
              key={sub.leaf}
              row={sub}
              topTotal={row.total}
              onClick={onSubClick ? () => onSubClick(sub.leaf) : undefined}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function SentimentRow({
  row,
  total,
  tone,
  onClick,
}: {
  row: SentimentLabelRow;
  total: number;
  tone: "negative" | "positive";
  onClick?: () => void;
}) {
  const ratio = total > 0 ? (row.count / total) * 100 : 0;
  return (
    <li>
      <button
        type="button"
        disabled={!onClick}
        onClick={onClick}
        className="-mx-2 flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-sm enabled:hover:bg-muted/60 enabled:cursor-pointer disabled:cursor-default text-left"
      >
        <span className="truncate">{row.label}</span>
        <div className="flex items-center gap-3 tabular-nums">
          <span className="text-xs text-muted-foreground">
            {ratio.toFixed(1)}%
          </span>
          <Badge
            variant="secondary"
            className={cn(
              "tabular-nums",
              tone === "negative" &&
                "bg-red-500/15 text-red-700 hover:bg-red-500/20",
              tone === "positive" &&
                "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20",
            )}
          >
            {formatNumber(row.count)}
          </Badge>
        </div>
      </button>
    </li>
  );
}

function SentimentDetail({
  title,
  data,
  tone,
  onClick,
}: {
  title: string;
  data: SentimentDistribution;
  tone: "negative" | "positive";
  onClick?: (label: string) => void;
}) {
  const coverage =
    data.userTotal > 0
      ? `${((data.total / data.userTotal) * 100).toFixed(1)}%`
      : "0%";
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3 border-b pb-3">
        <div className="space-y-0.5">
          <h3 className="text-base font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">
            전체 user 메시지 대비 {coverage} ·{" "}
            {formatNumber(data.total)} / {formatNumber(data.userTotal)}
          </p>
        </div>
      </div>

      {data.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          조건에 맞는 라벨이 없습니다.
        </p>
      ) : (
        <ul className="divide-y">
          {data.rows.map((row) => (
            <SentimentRow
              key={row.label}
              row={row}
              total={data.total}
              tone={tone}
              onClick={onClick ? () => onClick(row.label) : undefined}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function LabelExplorerBody({
  topRows,
  negative,
  positive,
  onTopClick,
  onSubClick,
  onNegativeClick,
  onPositiveClick,
}: {
  topRows: IntentTopBreakdownRow[];
  negative: SentimentDistribution;
  positive: SentimentDistribution;
  onTopClick?: (top: string) => void;
  onSubClick?: (leaf: string) => void;
  onNegativeClick?: (label: string) => void;
  onPositiveClick?: (label: string) => void;
}) {
  const sortedTop = useMemo(
    () =>
      [...topRows]
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total),
    [topRows],
  );

  const [rawSelection, setSelection] = useState<Selection>(
    sortedTop[0] ? { kind: "top", top: sortedTop[0].top } : { kind: "negative" },
  );

  const selection: Selection =
    rawSelection.kind !== "top" ||
    sortedTop.some((r) => r.top === rawSelection.top)
      ? rawSelection
      : sortedTop[0]
        ? { kind: "top", top: sortedTop[0].top }
        : { kind: "negative" };

  const selectedTop =
    selection.kind === "top"
      ? sortedTop.find((r) => r.top === selection.top) ?? null
      : null;

  if (
    sortedTop.length === 0 &&
    negative.rows.length === 0 &&
    positive.rows.length === 0
  ) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          조건에 맞는 라벨이 없습니다.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
      <nav className="space-y-3 md:border-r md:pr-3">
        <div className="space-y-1">
          {sortedTop.map((row) => (
            <NavItem
              key={row.top}
              label={row.topKo || row.top}
              count={row.total}
              active={
                selection.kind === "top" && selection.top === row.top
              }
              onSelect={() => setSelection({ kind: "top", top: row.top })}
            />
          ))}
        </div>

        <hr className="border-border" />

        <div className="space-y-1">
          <NavItem
            label="부정 신호"
            count={negative.total}
            tone="negative"
            active={selection.kind === "negative"}
            onSelect={() => setSelection({ kind: "negative" })}
          />
          <NavItem
            label="긍정 신호"
            count={positive.total}
            tone="positive"
            active={selection.kind === "positive"}
            onSelect={() => setSelection({ kind: "positive" })}
          />
        </div>
      </nav>

      <div className="md:pl-1">
        {selection.kind === "top" && selectedTop && (
          <SelectedTopDetail
            row={selectedTop}
            onTopClick={onTopClick}
            onSubClick={onSubClick}
          />
        )}
        {selection.kind === "top" && !selectedTop && (
          <p className="text-sm text-muted-foreground">
            왼쪽에서 라벨을 선택하세요.
          </p>
        )}
        {selection.kind === "negative" && (
          <SentimentDetail
            title="부정 신호"
            data={negative}
            tone="negative"
            onClick={onNegativeClick}
          />
        )}
        {selection.kind === "positive" && (
          <SentimentDetail
            title="긍정 신호"
            data={positive}
            tone="positive"
            onClick={onPositiveClick}
          />
        )}
      </div>
    </div>
  );
}

export function LabelExplorerLoadingFallback() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
      <div className="space-y-1 md:border-r md:pr-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
      <div className="space-y-3 md:pl-1">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  );
}

export function LabelExplorer({
  filters,
  onTopClick,
  onSubClick,
  onNegativeClick,
  onPositiveClick,
}: Props) {
  const topQuery = useSuspenseQuery(intentTopBreakdownQueryOptions(filters));
  const negQuery = useSuspenseQuery(negativeFeedbackQueryOptions(filters));
  const posQuery = useSuspenseQuery(positiveFeedbackQueryOptions(filters));

  return (
    <LabelExplorerBody
      topRows={topQuery.data}
      negative={negQuery.data}
      positive={posQuery.data}
      onTopClick={onTopClick}
      onSubClick={onSubClick}
      onNegativeClick={onNegativeClick}
      onPositiveClick={onPositiveClick}
    />
  );
}
